import { type MatchSummary } from "@/api/tournamentAnalysis";
import { Loader } from "../common/loader";

interface TournamentAnalysisGamesSummaryProps {
    matchSummary?: MatchSummary;
    isLoading?: boolean;
}

export function TournamentAnalysisGamesSummary({ matchSummary, isLoading }: TournamentAnalysisGamesSummaryProps) {

    const wins = matchSummary?.wins || 0;
    const winsOT = matchSummary?.winsOvertime || 0;
    const losses = matchSummary?.losses || 0;
    const lossesOT = matchSummary?.lossesOvertime || 0;
    const totalGames = wins + winsOT + losses + lossesOT;
    const winPct = totalGames > 0 ? ((wins + winsOT) / totalGames) * 100 : 0;

    const rows = [
        { label: "Wins", value: wins, color: "var(--color-general-green-to)" },
        { label: "Wins (OT)", value: winsOT, color: "var(--color-general-green)" },
        { label: "Losses", value: losses, color: "var(--color-general-red)" },
        { label: "Losses (OT)", value: lossesOT, color: "var(--color-general-red-background)" },
    ];

    if (isLoading && !matchSummary) {
        return (
            <div className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm h-full">
                <Loader message="Loading games..." />
            </div>
        );
    }

    if (!isLoading && !matchSummary) {
        return (
            <div className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm h-full">
                <p className="text-on-surface-variant">No tournament data available</p>
            </div>
        );
    }

    return (
        <div
            id="onboarding-tournament-games-summary"
            className="bg-surface-container-lowest rounded-xl overflow-auto shadow-sm h-full flex flex-col"
        >
            <table className="w-full border-collapse">
                <thead>
                    <tr>
                        {["Result", "GP"].map((label) => (
                            <th
                                key={label}
                                className="data-table-header px-4 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-left"
                            >
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label} className="data-table-row">
                            <td className="px-4 py-3 text-sm font-medium text-on-surface whitespace-nowrap">
                                <span
                                    className="inline-block w-2 h-2 rounded-full mr-2"
                                    style={{ background: row.color }}
                                />
                                {row.label}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-on-surface whitespace-nowrap">
                                {row.value}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="px-4 py-4 mt-auto border-t border-outline-variant/20">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-headline font-black uppercase tracking-widest section-title">
                        Win Rate
                    </span>
                    <span className="text-sm font-bold text-general-green">{winPct.toFixed(1)}%</span>
                </div>
                <div className="w-full rounded-full h-2 bg-outline-variant/30">
                    <div
                        className="h-2 rounded-full transition-all duration-500 bg-gradient-general-green"
                        style={{ width: `${winPct}%` }}
                    />
                </div>
                <p className="text-xs text-on-surface-variant mt-2">
                    {wins + winsOT} wins out of {totalGames} games
                </p>
            </div>
        </div>
    );
}
